import React from "react";
import { Box, Typography, Button } from "@mui/material";

export type TabType = "organizations" | "feedback" | "users" | "migration";

interface TopHeaderDerinProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  stats: {
    organizationsCount: number;
    usersCount: number;
  };
  feedbackCount?: number;
  /** Migration is admin-only, so the tab is only rendered when the caller allows it. */
  showMigrationTab?: boolean;
}

const TopHeaderDerin: React.FC<TopHeaderDerinProps> = ({
  activeTab,
  onTabChange,
  stats,
  feedbackCount = 0,
  showMigrationTab = false,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: "0px 39.5px 1px",
        background: "rgba(40, 178, 172, 0.04)",
        borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
        mx: -3, // Negative margin to extend to edges
        mt: -3,
        px: "39.5px",
      }}
    >
      {/* Inner Container */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          padding: "16px 24px 0px 0px",
          gap: "16px",
          width: "100%",
        }}
      >
        {/* Title and Stats Row */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          {/* Title Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: "20px",
                lineHeight: "36px",
                letterSpacing: "0.0703125px",
                color: "#0A0A0A",
              }}
            >
              Derin Admin Controls
            </Typography>
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "24px",
                letterSpacing: "-0.3125px",
                color: "#717182",
              }}
            >
              Manage client accounts, users, surveys, and feedback
            </Typography>
          </Box>

          {/* Stats */}
          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              fontSize: "16px",
              lineHeight: "24px",
              textAlign: "right",
              letterSpacing: "-0.3125px",
              color: "#717182",
            }}
          >
            {stats.organizationsCount} Organizations • {stats.usersCount} Users
          </Typography>
        </Box>

        {/* Tabs */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "5px",
            pb: 3,
          }}
        >
          <Button
            onClick={() => onTabChange("organizations")}
            sx={{
              height: "40px",
              px: "8px",
              background: activeTab === "organizations" ? "#002824" : "transparent",
              borderRadius: "8px",
              fontFamily: "Inter, sans-serif",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "24px",
              textAlign: "center",
              letterSpacing: "-0.3125px",
              color: activeTab === "organizations" ? "#FFFFFF" : "#0A0A0A",
              textTransform: "none",
              "&:hover": {
                background: activeTab === "organizations" ? "#003d38" : "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            Organizations
          </Button>

          <Box sx={{ position: "relative" }}>
            <Button
              onClick={() => onTabChange("feedback")}
              sx={{
                height: "40px",
                px: "16px",
                background: activeTab === "feedback" ? "#002824" : "transparent",
                borderRadius: "8px",
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: "16px",
                lineHeight: "24px",
                textAlign: "center",
                letterSpacing: "-0.3125px",
                color: activeTab === "feedback" ? "#FFFFFF" : "#0A0A0A",
                textTransform: "none",
                "&:hover": {
                  background: activeTab === "feedback" ? "#003d38" : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Feedback
            </Button>
            {feedbackCount > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "absolute",
                  width: "20px",
                  height: "20px",
                  right: "-4px",
                  top: "-4px",
                  background: "#D4183D",
                  borderRadius: "10px",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    fontSize: feedbackCount >= 10 ? "11px" : "16px",
                    lineHeight: feedbackCount >= 10 ? "16px" : "24px",
                    textAlign: "center",
                    letterSpacing: "-0.3125px",
                    color: "#FFFFFF",
                  }}
                >
                  {feedbackCount}
                </Typography>
              </Box>
            )}
          </Box>

          <Button
            onClick={() => onTabChange("users")}
            sx={{
              height: "40px",
              px: "16px",
              background: activeTab === "users" ? "#002824" : "transparent",
              borderRadius: "8px",
              fontFamily: "Inter, sans-serif",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "24px",
              textAlign: "center",
              letterSpacing: "-0.3125px",
              color: activeTab === "users" ? "#FFFFFF" : "#0A0A0A",
              textTransform: "none",
              "&:hover": {
                background: activeTab === "users" ? "#003d38" : "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            Users
          </Button>

          {/* Admin-only: surveys → collections backfill. The parent decides whether to
              render it (see AdminControlsPage). */}
          {showMigrationTab && (
            <Button
              onClick={() => onTabChange("migration")}
              sx={{
                height: "40px",
                px: "16px",
                background: activeTab === "migration" ? "#002824" : "transparent",
                borderRadius: "8px",
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: "16px",
                lineHeight: "24px",
                textAlign: "center",
                letterSpacing: "-0.3125px",
                color: activeTab === "migration" ? "#FFFFFF" : "#0A0A0A",
                textTransform: "none",
                "&:hover": {
                  background: activeTab === "migration" ? "#003d38" : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Migration
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TopHeaderDerin;
